import { useTheme } from "@/src/styles/theme-provider";
import { useEffect, useRef, useState } from "react";
import { Text, TextInput, View } from "react-native";

type DateInputProps = Readonly<{
  className?: string;
  initialDay?: string;
  initialMonth?: string;
  initialYear?: string;
  onChange?: (day: string, month: string, year: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}>;

const MONTH_NAMES: Record<number, string> = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const SHORT_MONTHS = [4, 6, 9, 11];

const getDaysInMonth = (month: string, year: string): number => {
  const parsedMonth = parseInt(month);
  const parsedYear = parseInt(year);
  if (!parsedMonth) return 31;
  if (parsedMonth === 2) {
    const isLeapYear =
      !isNaN(parsedYear) &&
      parsedYear % 4 === 0 &&
      (parsedYear % 100 !== 0 || parsedYear % 400 === 0);
    return isLeapYear ? 29 : 28;
  }
  return SHORT_MONTHS.includes(parsedMonth) ? 30 : 31;
};

const clampDay = (day: string, month: string, year: string): string => {
  const maxDays = getDaysInMonth(month, year);
  return parseInt(day) > maxDays ? String(maxDays) : day;
};

const DateInput = ({
  className = "",
  initialDay = "",
  initialMonth = "",
  initialYear = "",
  onChange,
  onFocus,
  onBlur,
}: DateInputProps) => {
  const { themeVars } = useTheme();
  const [day, setDay] = useState(initialDay);
  const [monthRaw, setMonthRaw] = useState(initialMonth);
  const [month, setMonth] = useState(initialMonth);
  const [year, setYear] = useState(initialYear);

  const dayRef = useRef<TextInput>(null);
  const monthRef = useRef<TextInput>(null);
  const yearRef = useRef<TextInput>(null);

  // Sync from external (e.g. form load) only when a real value arrives. Without
  // this guard, an incomplete date round-trips back as empty strings and clears
  // the fields mid-typing, so a new date can never be entered.
  useEffect(() => {
    if (initialDay || initialMonth || initialYear) {
      setDay(initialDay);
      setMonthRaw(initialMonth);
      setMonth(initialMonth);
      setYear(initialYear);
    }
  }, [initialDay, initialMonth, initialYear]);

  const handleChange = (newDay: string, newMonth: string, newYear: string) => {
    onChange?.(newDay, newMonth, newYear);
  };

  const handleDay = (text: string) => {
    const numeric = text.replace(/[^0-9]/g, "");
    const validDay = clampDay(numeric, month, year);
    setDay(validDay);
    handleChange(validDay, month, year);
    if (validDay.length === 2) monthRef.current?.focus();
  };

  const handleMonth = (text: string) => {
    const numeric = text.replace(/[^0-9]/g, "");
    const validMonth = parseInt(numeric) > 12 ? "12" : numeric;
    setMonthRaw(validMonth);
    setMonth(validMonth);
    const clampedDay = clampDay(day, validMonth, year);
    setDay(clampedDay);
    handleChange(clampedDay, validMonth, year);
    const parsedMonth = parseInt(validMonth);
    if (
      validMonth.length === 2 ||
      (validMonth.length === 1 && parsedMonth >= 2)
    ) {
      yearRef.current?.focus();
    }
  };

  const handleYear = (text: string) => {
    const numeric = text.replace(/[^0-9]/g, "");
    setYear(numeric);
    if (month === "2") {
      const clampedDay = clampDay(day, month, numeric);
      setDay(clampedDay);
      handleChange(clampedDay, month, numeric);
    } else {
      handleChange(day, month, numeric);
    }
  };

  const monthDisplay =
    MONTH_NAMES[parseInt(month)] && (month.length === 2 || parseInt(month) >= 2)
      ? MONTH_NAMES[parseInt(month)]
      : monthRaw;

  const inputClass =
    "text-center text-base text-onyx focus:outline-none p-0 m-0";

  return (
    <View className={`flex-row items-center gap-1 ${className}`}>
      <TextInput
        ref={dayRef}
        className={`w-9 ${inputClass}`}
        placeholder="DD"
        placeholderTextColor={themeVars["--color-ash-1"] as string}
        value={day}
        onChangeText={handleDay}
        keyboardType="numeric"
        maxLength={2}
        onFocus={onFocus}
        onBlur={onBlur}
      />

      <Text className="text-ash-1 text-base">/</Text>

      <TextInput
        ref={monthRef}
        className={`w-10 ${inputClass}`}
        placeholder="MM"
        placeholderTextColor={themeVars["--color-ash-1"] as string}
        value={monthDisplay}
        onChangeText={handleMonth}
        keyboardType="numeric"
        maxLength={2}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyPress={({ nativeEvent }) => {
          if (nativeEvent.key === "Backspace" && monthRaw === "") {
            dayRef.current?.focus();
          }
        }}
      />

      <Text className="text-ash-1 text-base">/</Text>

      <TextInput
        ref={yearRef}
        className={`w-14 ${inputClass}`}
        placeholder="YYYY"
        placeholderTextColor={themeVars["--color-ash-1"] as string}
        value={year}
        onChangeText={handleYear}
        keyboardType="numeric"
        maxLength={4}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyPress={({ nativeEvent }) => {
          if (nativeEvent.key === "Backspace" && year === "") {
            monthRef.current?.focus();
          }
        }}
      />
    </View>
  );
};
export default DateInput;
